import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle, Target, FileText } from "lucide-react";

// Define a view type for the result to avoid unknown in JSX
type CasePredictionView = {
  case_type?: string;
  jurisdiction?: string;
  evidence_strength?: string | number;
  budget?: string | number;
  description?: string;
  predictionScore: number;
  analysis?: {
    strengths?: string[];
    weaknesses?: string[];
    risks?: string[];
    opportunities?: string[];
  };
  recommendations?: string[];
};

export default function CasePredictionResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const result = location.state?.result as CasePredictionView | undefined;

  if (!result) {
    navigate("/dashboard/agents/litigation-predictor");
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <h1 className="text-3xl font-bold tracking-tight">
          Resultados de Predicción
        </h1>
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200">
          Generado
        </Badge>
      </div>

      <div className="rounded-xl overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-indigo-900 to-blue-700 text-white p-6">
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-1">
              Análisis Predictivo de Caso Legal
            </h2>
            <p className="text-white/80">
              Resultado basado en su información y datos históricos
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Información del Caso</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p>
              <strong>Tipo:</strong> {result.case_type}
            </p>
            <p>
              <strong>Jurisdicción:</strong> {result.jurisdiction}
            </p>
            <p>
              <strong>Evidencia:</strong> {result.evidence_strength}
            </p>
            <p>
              <strong>Presupuesto:</strong> {result.budget}
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Resumen</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <p className="text-muted-foreground">{result.description}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl p-6 text-white bg-gradient-to-br from-indigo-500 to-purple-600 shadow-sm">
          <div className="text-4xl font-bold mb-1">
            {result.predictionScore}%
          </div>
          <div className="opacity-90">Probabilidad de Éxito</div>
        </div>
        <div className="rounded-xl p-6 text-white bg-gradient-to-br from-blue-500 to-cyan-600 shadow-sm">
          <div className="text-4xl font-bold mb-1">
            {Math.max(3, Math.round((100 - result.predictionScore) / 3))}
          </div>
          <div className="opacity-90">Meses Estimados</div>
        </div>
        <div className="rounded-xl p-6 text-white bg-gradient-to-br from-emerald-500 to-teal-600 shadow-sm">
          <div className="text-4xl font-bold mb-1">
            {Math.min(95, Math.round(result.predictionScore * 1.1))}%
          </div>
          <div className="opacity-90">Recuperación Esperada</div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="text-sm text-muted-foreground mb-2">Índice global</div>
        <Progress value={result.predictionScore} className="h-2" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {result.analysis?.strengths?.length > 0 && (
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" /> Factores Favorables
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {result.analysis.strengths.map((item: string, idx: number) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 bg-green-50 px-3 py-2 rounded">
                    <span className="text-green-600 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {(result.analysis?.weaknesses?.length > 0 ||
          result.analysis?.risks?.length > 0) && (
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-4 w-4" /> Factores de Riesgo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {[
                  ...(result.analysis.weaknesses || []),
                  ...(result.analysis.risks || []),
                ].map((item: string, idx: number) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 bg-red-50 px-3 py-2 rounded">
                    <span className="text-red-600 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {result.analysis?.opportunities?.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-blue-700">
              <Target className="h-4 w-4" /> Oportunidades
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {result.analysis.opportunities.map(
                (item: string, idx: number) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 bg-blue-50 px-3 py-2 rounded">
                    <span className="text-blue-600 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                )
              )}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="rounded-xl p-6 text-white bg-gradient-to-r from-pink-400 to-rose-500 shadow-sm">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4" /> Recomendaciones Estratégicas
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {result.recommendations?.map((rec: string, idx: number) => (
            <div
              key={idx}
              className="bg-white/10 border border-white/20 rounded-lg p-3 text-sm">
              {rec}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="outline" onClick={() => navigate(-1)}>
          Volver
        </Button>
      </div>
    </div>
  );
}
